<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">현장 목록</h1>
            <p class="text-sm text-slate-500">등록된 분양 현장을 관리합니다</p>
          </div>
        </div>
        <router-link
          to="/admin/properties/create"
          class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 transition-all"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          새 현장 등록
        </router-link>
      </div>

      <!-- 필터 & 검색 -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-6">
        <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <!-- 상태 필터 -->
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="status in statuses"
              :key="status.value"
              @click="filterStatus = status.value"
              :class="[
                'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                filterStatus === status.value
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              ]"
            >
              {{ status.label }}
              <span
                v-if="status.value !== 'all'"
                :class="[
                  'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                  filterStatus === status.value ? 'bg-white/20' : 'bg-slate-200'
                ]"
              >
                {{ getStatusCount(status.value) }}
              </span>
            </button>
          </div>

          <!-- 검색 -->
          <div class="flex-1 w-full sm:w-auto sm:min-w-[280px]">
            <div class="relative">
              <svg class="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="현장명 또는 주소로 검색..."
                class="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p class="text-slate-500">현장 목록을 불러오는 중...</p>
      </div>

      <!-- 현장 리스트 (테이블 형태) -->
      <div v-else-if="filteredProperties.length > 0" class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <!-- 테이블 헤더 -->
        <div class="hidden md:grid md:grid-cols-12 gap-4 px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <div class="col-span-4">현장명</div>
          <div class="col-span-2">상태</div>
          <div class="col-span-2">세대수</div>
          <div class="col-span-2">입주예정</div>
          <div class="col-span-2 text-right">관리</div>
        </div>

        <!-- 테이블 바디 -->
        <div class="divide-y divide-slate-100">
          <div
            v-for="property in filteredProperties"
            :key="property.id"
            class="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-4 hover:bg-slate-50 transition group"
          >
            <!-- 현장명 + 주소 -->
            <div class="col-span-1 md:col-span-4">
              <h3 class="font-semibold text-slate-800 group-hover:text-blue-600 transition">{{ property.name }}</h3>
              <p class="text-sm text-slate-500 mt-0.5 flex items-center gap-1">
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span class="truncate">{{ property.address || '주소 미입력' }}</span>
              </p>
              <!-- 모바일에서 추가 정보 -->
              <div class="flex flex-wrap items-center gap-2 mt-2 md:hidden">
                <span :class="['px-2.5 py-1 rounded-full text-xs font-medium', getStatusBadgeClass(property.status)]">
                  {{ getStatusLabel(property.status) }}
                </span>
                <span v-if="property.totalUnits" class="text-xs text-slate-500">{{ property.totalUnits }}세대</span>
                <span v-if="property.moveInDate" class="text-xs text-slate-500">{{ formatMoveInDate(property.moveInDate) }} 입주</span>
              </div>
            </div>

            <!-- 상태 -->
            <div class="hidden md:flex md:col-span-2 items-center">
              <span :class="['px-2.5 py-1 rounded-full text-xs font-medium', getStatusBadgeClass(property.status)]">
                {{ getStatusLabel(property.status) }}
              </span>
            </div>

            <!-- 세대수 -->
            <div class="hidden md:flex md:col-span-2 items-center text-sm text-slate-600">
              {{ property.totalUnits ? `${property.totalUnits.toLocaleString()}세대` : '-' }}
            </div>

            <!-- 입주예정 -->
            <div class="hidden md:flex md:col-span-2 items-center text-sm text-slate-600">
              {{ property.moveInDate ? formatMoveInDate(property.moveInDate) : '-' }}
            </div>

            <!-- 액션 버튼 -->
            <div class="col-span-1 md:col-span-2 flex items-center justify-end gap-2 mt-3 md:mt-0">
              <router-link
                :to="`/admin/properties/${property.id}/edit`"
                class="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-600 hover:text-white transition"
              >
                수정
              </router-link>
              <button
                @click="confirmDelete(property)"
                class="px-3 py-1.5 text-sm font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-500 hover:text-white transition"
              >
                삭제
              </button>
            </div>
          </div>
        </div>

        <!-- 하단 카운트 -->
        <div class="px-5 py-3 bg-slate-50 border-t border-slate-100 text-sm text-slate-500">
          총 <span class="font-semibold text-slate-700">{{ filteredProperties.length }}</span>개 현장
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
        <div class="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">등록된 현장이 없습니다</h3>
        <p class="text-slate-500 mb-6">첫 번째 분양 현장을 등록해보세요!</p>
        <router-link
          to="/admin/properties/create"
          class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/25 transition-all"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          첫 현장 등록하기
        </router-link>
      </div>

      <!-- 삭제 확인 모달 -->
      <Teleport to="body">
        <div
          v-if="showDeleteModal"
          class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          @click="showDeleteModal = false"
        >
          <div
            class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            @click.stop
          >
            <div class="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-800 text-center mb-2">현장 삭제</h3>
            <p class="text-slate-600 text-center mb-6">
              <span class="font-semibold text-slate-800">"{{ deleteTarget?.name }}"</span><br/>
              현장을 정말 삭제하시겠습니까?
            </p>
            <div class="flex gap-3">
              <button
                @click="showDeleteModal = false"
                class="flex-1 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition"
              >
                취소
              </button>
              <button
                @click="handleDelete"
                :disabled="deleting"
                class="flex-1 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition disabled:opacity-50"
              >
                {{ deleting ? '삭제 중...' : '삭제하기' }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useProperty } from '@/composables/admin/useProperty'

export default {
  name: 'PropertyListView',
  components: {
    AdminLayout
  },
  setup() {
    const { properties, loading, fetchProperties, deleteProperty } = useProperty()

    const filterStatus = ref('all')
    const searchQuery = ref('')
    const showDeleteModal = ref(false)
    const deleteTarget = ref(null)
    const deleting = ref(false)

    const statuses = [
      { value: 'all', label: '전체' },
      { value: 'upcoming', label: '분양 예정' },
      { value: 'ongoing', label: '분양 중' },
      { value: 'completed', label: '분양 완료' }
    ]

    const getStatusCount = (status) => {
      return properties.value.filter(p => p.status === status).length
    }

    const filteredProperties = computed(() => {
      return properties.value.filter((property) => {
        // 상태 필터
        if (filterStatus.value !== 'all' && property.status !== filterStatus.value) {
          return false
        }

        // 검색 필터
        if (searchQuery.value) {
          const query = searchQuery.value.toLowerCase()
          const matchName = property.name?.toLowerCase().includes(query)
          const matchAddress = property.address?.toLowerCase().includes(query)
          if (!matchName && !matchAddress) {
            return false
          }
        }

        return true
      })
    })

    const getStatusLabel = (status) => {
      const labels = {
        upcoming: '분양 예정',
        ongoing: '분양 중',
        completed: '분양 완료'
      }
      return labels[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        upcoming: 'bg-amber-100 text-amber-700 border border-amber-200',
        ongoing: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
        completed: 'bg-slate-100 text-slate-600 border border-slate-200'
      }
      return classes[status] || 'bg-slate-100 text-slate-600 border border-slate-200'
    }

    const formatMoveInDate = (dateStr) => {
      if (!dateStr) return ''
      const [year, month] = dateStr.split('-')
      return `${year}.${month}`
    }

    const confirmDelete = (property) => {
      deleteTarget.value = property
      showDeleteModal.value = true
    }

    const handleDelete = async () => {
      if (!deleteTarget.value) return

      deleting.value = true
      const result = await deleteProperty(deleteTarget.value.id)
      deleting.value = false

      if (result.success) {
        showDeleteModal.value = false
        deleteTarget.value = null
      } else {
        alert('삭제에 실패했습니다.')
      }
    }

    onMounted(() => {
      fetchProperties()
    })

    return {
      properties,
      loading,
      filterStatus,
      searchQuery,
      statuses,
      filteredProperties,
      showDeleteModal,
      deleteTarget,
      deleting,
      getStatusCount,
      getStatusLabel,
      getStatusBadgeClass,
      formatMoveInDate,
      confirmDelete,
      handleDelete
    }
  }
}
</script>
